import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { useToast } from "@/components/base/Toast";

/** Compte dont le mot de passe provisoire vient d'être généré. */
export interface TemporaryAccount {
  email: string;
  firstName: string;
  lastName: string;
  temporaryPassword: string;
  /** true : les identifiants ont aussi été envoyés par e-mail à l'intéressé. */
  emailSent?: boolean;
}

interface TemporaryPasswordDialogProps {
  /** Compte concerné (null = fermé). */
  admin: TemporaryAccount | null;
  /** Contexte affiché sous le titre (nom de l'école, du compte...). */
  schoolName: string;
  title?: string;
  onClose: () => void;
}

/**
 * Identifiants d'un compte dont le mot de passe provisoire vient d'être généré (ouverture d'école,
 * création ou réinitialisation d'un compte). Le mot de passe n'est affiché qu'ici, une seule fois :
 * il sera exigé de le changer à la première connexion.
 */
export default function TemporaryPasswordDialog({
  admin,
  schoolName,
  title = "Compte administrateur créé",
  onClose,
}: TemporaryPasswordDialogProps) {
  const { notify } = useToast();
  const copy = async () => {
    if (!admin) return;
    try {
      await navigator.clipboard.writeText(
        `${schoolName}\nIdentifiant : ${admin.email}\nMot de passe provisoire : ${admin.temporaryPassword}`,
      );
      notify("Identifiants copiés.");
    } catch {
      notify("Copie impossible : notez les identifiants manuellement.", "error");
    }
  };

  return (
    <Modal
      open={admin !== null}
      onClose={onClose}
      title={title}
      subtitle={schoolName}
      icon="ri-key-2-line"
      size="sm"
      footer={
        <>
          <Button variant="outline" icon="ri-file-copy-line" onClick={copy}>
            Copier
          </Button>
          <Button onClick={onClose}>J'ai noté les identifiants</Button>
        </>
      }
    >
      {admin && (
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">
            Transmettez ces identifiants à {admin.firstName} {admin.lastName}. Le mot de passe
            provisoire devra être changé à la première connexion.
          </p>
          <dl className="space-y-2 rounded-lg border border-background-200 bg-background-100/60 p-4 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-500">Identifiant</dt>
              <dd className="font-medium text-foreground-900 break-all">{admin.email}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-500">Mot de passe provisoire</dt>
              <dd className="font-mono font-semibold tracking-wide text-primary-800">{admin.temporaryPassword}</dd>
            </div>
          </dl>
          {admin.emailSent ? (
            <p className="flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-800">
              <i className="ri-mail-check-line text-sm"></i>
              Ces identifiants ont aussi été envoyés par e-mail à {admin.email}.
            </p>
          ) : (
            <p className="flex items-start gap-2 rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-600">
              <i className="ri-mail-close-line text-sm"></i>
              L'e-mail n'a pas pu être envoyé : transmettez ces identifiants vous-même.
            </p>
          )}
          <p className="flex items-start gap-2 text-xs text-accent-700">
            <i className="ri-error-warning-line text-sm"></i>
            Ce mot de passe ne sera plus affiché après la fermeture de cette fenêtre.
          </p>
        </div>
      )}
    </Modal>
  );
}
